import { faker } from '@faker-js/faker';
import type { ComplianceUserMetric, MemoryState } from 'api/users/compliance.api';
import type { ItemStatusWrapper } from 'components/users/flexlist/FlexList';
import { READY } from 'components/users/flexlist/FlexList';
import type { HoursPerWeekData } from 'components/users/HoursLoggedPerWeekGraph';
import type { TeamUser, TeamWithUsers } from 'factories/teams';
import { DateTime } from 'luxon';
import { AnomalyActivityTypes, RegularActivityTypes } from 'redux/modules/activities.types';
import {
  DEMO_GENERATOR_SEED,
  DEMO_ID_START,
  DEMO_INVITED_COUNT,
  DEMO_TEAM_SIZE,
  demoIdRange,
} from 'redux/modules/users-page/demo/usersPage.demo.constants';
import { DEMO_NAMES } from 'redux/modules/users-page/demo/usersPage.demo.names.generated';
import type {
  UserSummaryView,
  UserViewModel,
  WeekSummaryWithRange,
} from 'redux/modules/users-page/users-dashboard/usersDashboard.types';
import type { ControlData, ListUser } from 'redux/modules/users-page/users-list/usersList.types';
import type { Activity } from 'types/activity';
import type { DurationSummary } from 'types/summaries';
import type { Team } from 'types/team';
import type { Duration } from 'types/units';
import type { EnrichedUser } from 'types/user';
import { UserTimeFormat } from 'types/user';
import { fromISO } from 'utils/luxon-helpers';
import type { CostAndDurationSummary } from 'utils/summary';

/**
 * Fictional People-page roster for the Manager demo preset, generated
 * procedurally from a fixed seed instead of hardcoded, and consumed
 * directly by `usersPage.demo.selectors.ts`.
 *
 * Roster + aggregate tab data are generated up front, in one seeded pass,
 * and memoized by (seed, size) — that's `getRosterBundle` below. Per-person
 * deep data (Timesheets/Report) is generated lazily, only for a demo id
 * that's actually opened, and memoized per id — see `getOrBuildDeepPersonView`.
 */

function makeInitialsAvatar(name: string): string {
  const parts = name.trim().split(' ');
  const initials = (
    parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')
  ).toUpperCase();
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><rect width="40" height="40" fill="%2344515e" rx="4"/><text x="20" y="27" text-anchor="middle" fill="white" font-family="sans-serif" font-size="14" font-weight="700">${initials}</text></svg>`;
  return `data:image/svg+xml,${svg}`;
}

/**
 * `UserRoute.tsx` renders a team's `emoji` field directly as `<img src=…>`,
 * so a plain emoji character there fires a broken image request (the string
 * gets treated as a URL). A data-URI SVG with the emoji as text renders
 * correctly through that same `<img>` tag without needing to touch the
 * (shared, out of scope here) rendering code.
 */
function makeEmojiImage(emoji: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><text x="12" y="18" text-anchor="middle" font-size="18">${emoji}</text></svg>`;
  return `data:image/svg+xml,${svg}`;
}

/**
 * Names come from the committed, pre-generated list rather than faker's
 * locale-specific instances at runtime: those 25 locale packages are only
 * needed to PRODUCE the names (`usersPage.demo.names.source.ts`, run via
 * `yarn generate-demo-names`), and importing them here pulled that cost into
 * every page's bundle, not just the People page (TT-5152 review). Indices
 * beyond the committed list mean a roster size grew without regenerating —
 * see `getDemoTeamSizeForPreset` in usersPage.demo.constants.ts.
 */
function nameForIndex(index: number): string {
  const name = DEMO_NAMES[index];
  if (name === undefined) {
    throw new Error(
      `usersPage.demo.generator: no committed name for index ${index} — run \`yarn generate-demo-names\` after raising DEMO_TEAM_SIZE/DEMO_INVITED_COUNT.`
    );
  }
  return name;
}

function makeDuration(totalMinutes: number): Duration {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return {
    hours: h,
    minutes: m,
    seconds: 0,
    formatted: m > 0 ? `${h}h ${m}m` : `${h}h`,
    total_hours: totalMinutes / 60,
    total_seconds: totalMinutes * 60,
    total_minutes: totalMinutes,
  };
}

const ZERO = makeDuration(0);
const WEEKS_GENERATED = 12;

export interface DemoUserPattern {
  id: number;
  name: string;
  userLevel: string;
  weeklyCapacityMinutes: number;
  weekMinutes: number[];
  dailyHoursMonToSun: number[];
}

/**
 * Persona categories (steady, overtime, missing, near-perfect, declining,
 * excused, never-logs) the generated roster covers, so it shows the same
 * spread of situations instead of a flat distribution. Which archetype a
 * person gets is a
 * weighted random pick (see ARCHETYPE_WEIGHTS below), not a fixed rotation —
 * cycling through the list in order put the two always-zero-hours
 * archetypes (excused, never-logs) at fixed, evenly-spaced positions, which
 * reads as an obviously generated pattern rather than a real team's shape.
 * Within an archetype, every person still samples their own numbers —
 * capacity, a weekly-hours band, an optional trend, and how many weekdays
 * they typically log on — so two people sharing an archetype don't share
 * numbers either.
 */
interface Weighted<T> {
  value: T;
  weight: number;
}

interface PersonaArchetype {
  key: string;
  // Weekly capacity choices in hours; one is sampled per person.
  capacityHoursOptions: number[];
  // Typical weekly logged-hours band this person's weeks are sampled around.
  weeklyHoursRange: [number, number];
  // 'declining' shrinks that band toward ~0 across the 12 generated weeks
  // (oldest → newest); 'flat' keeps it steady week to week.
  trend: 'flat' | 'declining';
  // How many of the 5 weekdays they typically log hours on.
  activeDaysRange: [number, number];
  levelWeights: Array<Weighted<string>>;
}

const STEADY: PersonaArchetype = {
  key: 'steady',
  capacityHoursOptions: [40],
  // Floored at 37h (≤7.5% shortfall on a 40h capacity) so this archetype
  // reliably reads as compliant under the default 10%-shortfall risk
  // threshold rather than a coin flip between compliant and at-risk.
  weeklyHoursRange: [37, 40],
  trend: 'flat',
  activeDaysRange: [4, 5],
  levelWeights: [
    { value: 'member', weight: 0.85 },
    { value: 'manager', weight: 0.15 },
  ],
};

const OVERTIME: PersonaArchetype = {
  key: 'overtime',
  capacityHoursOptions: [40],
  weeklyHoursRange: [44, 52],
  trend: 'flat',
  activeDaysRange: [5, 5],
  levelWeights: [{ value: 'member', weight: 1 }],
};

const MISSING: PersonaArchetype = {
  key: 'missing',
  capacityHoursOptions: [32, 40],
  weeklyHoursRange: [3, 16],
  trend: 'flat',
  activeDaysRange: [1, 3],
  levelWeights: [{ value: 'member', weight: 1 }],
};

const NEAR_PERFECT: PersonaArchetype = {
  key: 'near-perfect',
  capacityHoursOptions: [40],
  weeklyHoursRange: [37, 42],
  trend: 'flat',
  activeDaysRange: [5, 5],
  levelWeights: [
    { value: 'member', weight: 0.7 },
    { value: 'manager', weight: 0.3 },
  ],
};

const DECLINING: PersonaArchetype = {
  key: 'declining',
  capacityHoursOptions: [40],
  weeklyHoursRange: [38, 45],
  // See generateWeekMinutes: the trend bottoms out at 80% of this person's
  // starting hours, not near zero — a flag worth catching early, not a
  // stopped-logging case (that's NEVER_LOGS/MISSING's job).
  trend: 'declining',
  activeDaysRange: [3, 5],
  levelWeights: [
    { value: 'manager', weight: 0.6 },
    { value: 'member', weight: 0.4 },
  ],
};

// On leave / excused — zero capacity keeps this person out of
// non-compliance everywhere.
const EXCUSED: PersonaArchetype = {
  key: 'excused',
  capacityHoursOptions: [0],
  weeklyHoursRange: [0, 0],
  trend: 'flat',
  activeDaysRange: [0, 0],
  levelWeights: [{ value: 'member', weight: 1 }],
};

const NEVER_LOGS: PersonaArchetype = {
  key: 'never-logs',
  capacityHoursOptions: [32, 40],
  weeklyHoursRange: [0, 0],
  trend: 'flat',
  activeDaysRange: [0, 0],
  levelWeights: [{ value: 'member', weight: 1 }],
};

// A best-case-but-still-believable team, not a statistically average one:
// this drives a sales/product demo, so the roster should read as "a
// well-run team the product keeps that way," with the flagged minority
// there to show the product catching real (explainable) cases rather than
// painting the whole workspace as struggling.
const ARCHETYPE_WEIGHTS: Array<Weighted<PersonaArchetype>> = [
  { value: STEADY, weight: 0.4 },
  { value: NEAR_PERFECT, weight: 0.3 },
  { value: OVERTIME, weight: 0.14 },
  { value: MISSING, weight: 0.06 },
  { value: DECLINING, weight: 0.06 },
  { value: EXCUSED, weight: 0.02 },
  { value: NEVER_LOGS, weight: 0.02 },
];

function pickWeighted<T>(options: Array<Weighted<T>>): T {
  const total = options.reduce((sum, o) => sum + o.weight, 0);
  let r = faker.number.float({ min: 0, max: total });
  for (const o of options) {
    if (r < o.weight) return o.value;
    r -= o.weight;
  }
  return options[options.length - 1].value;
}

/**
 * One archetype per roster slot. A pure weighted draw for every person can
 * — for any given fixed seed — land on zero people for a whole archetype
 * (e.g. nobody "on leave" at all), which would undercut "one roster covers
 * the full spread of situations." So every archetype gets at least one
 * guaranteed slot when the roster is
 * large enough to fit one of each; the rest fill in by weighted draw. The
 * combined list is then shuffled, so the guaranteed slots land at random
 * positions rather than fixed, evenly-spaced ones.
 */
function assignArchetypes(size: number): PersonaArchetype[] {
  const allArchetypes = ARCHETYPE_WEIGHTS.map(a => a.value);
  const assignments =
    size >= allArchetypes.length
      ? [...allArchetypes]
      : Array.from({ length: size }, () => pickWeighted(ARCHETYPE_WEIGHTS));
  while (assignments.length < size) {
    assignments.push(pickWeighted(ARCHETYPE_WEIGHTS));
  }
  return faker.helpers.shuffle(assignments);
}

/** This person's 12-week logged-hours series (oldest → newest), sampled
 *  independently around the archetype's band with per-week noise, and
 *  shrunk toward zero across the window for a 'declining' trend. */
function generateWeekMinutes(archetype: PersonaArchetype): number[] {
  const [rangeMin, rangeMax] = archetype.weeklyHoursRange;
  if (rangeMax === 0) return Array(WEEKS_GENERATED).fill(0);

  const baseHours = faker.number.float({ min: rangeMin, max: rangeMax, fractionDigits: 1 });
  const weekMinutes: number[] = [];
  for (let w = 0; w < WEEKS_GENERATED; w++) {
    const progress = w / (WEEKS_GENERATED - 1);
    const trendFactor = archetype.trend === 'declining' ? 1 - 0.2 * progress : 1;
    const noise = faker.number.float({ min: -2, max: 2, fractionDigits: 1 });
    const hours = Math.max(0, baseHours * trendFactor + noise);
    weekMinutes.push(Math.round(hours * 60));
  }
  return weekMinutes;
}

/** No single logged day reads as plausible past this, whatever the week's
 *  total — caps the per-day split below so a high weekly total spreads over
 *  more days instead of piling into one implausible one. */
const MAX_PLAUSIBLE_DAILY_HOURS = 11;

/** This person's typical weekday shape, split across a randomly chosen
 *  subset of weekdays with modest per-day variation so it sums to their most
 *  recent week's total — every person gets a distinct shape, no single day
 *  reads as implausible, and the daily control grid always agrees with the
 *  week it's drawn from. */
function generateDailyHoursMonToSun(
  archetype: PersonaArchetype,
  lastWeekMinutes: number
): number[] {
  const dailyHoursMonToSun = [0, 0, 0, 0, 0, 0, 0];
  const lastWeekHours = lastWeekMinutes / 60;
  if (lastWeekHours <= 0) return dailyHoursMonToSun;

  // At least enough active days that an even split alone stays plausible,
  // so the weighted variation below can't push a day past the cap.
  const minPlausibleDays = Math.ceil(lastWeekHours / MAX_PLAUSIBLE_DAILY_HOURS);
  const activeDaysCount = Math.min(
    5,
    Math.max(
      minPlausibleDays,
      faker.number.int({
        min: Math.min(archetype.activeDaysRange[0], 5),
        max: Math.min(archetype.activeDaysRange[1], 5),
      })
    )
  );
  if (activeDaysCount <= 0) return dailyHoursMonToSun;

  const activeWeekdayIndices = faker.helpers.shuffle([0, 1, 2, 3, 4]).slice(0, activeDaysCount);
  // Modest per-day variation (±20%) around an even split — enough for a
  // distinct shape per person without spiking any single day.
  const weights = activeWeekdayIndices.map(() => faker.number.float({ min: 0.8, max: 1.2 }));
  const totalWeight = weights.reduce((sum, w) => sum + w, 0);
  activeWeekdayIndices.forEach((dayIndex, k) => {
    const hours = Math.min(MAX_PLAUSIBLE_DAILY_HOURS, (lastWeekHours * weights[k]) / totalWeight);
    dailyHoursMonToSun[dayIndex] = Math.round(hours * 10) / 10;
  });
  return dailyHoursMonToSun;
}

interface GeneratedPerson {
  pattern: DemoUserPattern;
  archetypeKey: string;
}

/** Roster generation plus which archetype each person landed on — kept
 *  internal (not part of `DemoUserPattern`) so `generateActivities` can pick
 *  one representative per situation actually present, without the
 *  interface needing to expose that concept at all. */
function generateDemoPeople(seed: number, size: number): GeneratedPerson[] {
  faker.seed(seed);
  const archetypeAssignments = assignArchetypes(size);
  const people: GeneratedPerson[] = [];
  for (let i = 0; i < size; i++) {
    const archetype = archetypeAssignments[i];
    const weekMinutes = generateWeekMinutes(archetype);
    // One admin at the head of the roster; everyone else samples a level
    // from their archetype's weights.
    const userLevel = i === 0 ? 'admin' : pickWeighted(archetype.levelWeights);
    people.push({
      pattern: {
        id: DEMO_ID_START + i,
        name: nameForIndex(i),
        userLevel,
        weeklyCapacityMinutes: faker.helpers.arrayElement(archetype.capacityHoursOptions) * 60,
        weekMinutes,
        dailyHoursMonToSun: generateDailyHoursMonToSun(archetype, weekMinutes[WEEKS_GENERATED - 1]),
      },
      archetypeKey: archetype.key,
    });
  }
  return people;
}

export function generateDemoRoster(seed: number, size: number): DemoUserPattern[] {
  return generateDemoPeople(seed, size).map(p => p.pattern);
}

export interface DemoInvitedPerson {
  id: number;
  name: string;
  invitedDaysAgo: number;
}

/**
 * Fictional pending invites for the Invited tab — never part of the active
 * roster, so they carry no hours/team/compliance data. Ids and names
 * continue the same deterministic sequence right after the active roster
 * (index DEMO_TEAM_SIZE onward), so they never collide with it.
 */
function generateInvitedPeople(seed: number, count: number): DemoInvitedPerson[] {
  faker.seed(seed + 5);
  return Array.from({ length: count }, (_, i) => {
    const rosterIndex = DEMO_TEAM_SIZE + i;
    return {
      id: DEMO_ID_START + rosterIndex,
      name: nameForIndex(rosterIndex),
      invitedDaysAgo: faker.number.int({ min: 1, max: 14 }),
    };
  });
}

interface DemoProject {
  id: number;
  name: string;
  color: string;
  clientId: number;
  clientName: string;
}

const PROJECT_COLORS = ['#4a9eff', '#f5a623', '#7ed321', '#bd10e0', '#50e3c2'];

function generateProjects(seed: number): DemoProject[] {
  faker.seed(seed + 1);
  const count = 3;
  const projects: DemoProject[] = [];
  for (let i = 0; i < count; i++) {
    projects.push({
      id: 2001 + i,
      name: faker.commerce.productName(),
      color: PROJECT_COLORS[i % PROJECT_COLORS.length],
      clientId: 1001 + i,
      clientName: faker.company.name(),
    });
  }
  return projects;
}

interface DemoComplianceMetric {
  userId: number;
  name: string;
  weeklyCapacityHours: number;
  totalHours: number;
  automationRate: number;
  memoryState: MemoryState;
  lastLoggedAgo: { hours?: number; days?: number; minutes?: number } | null;
  lastSeenAgo: { hours?: number; days?: number } | null;
}

const MEMORY_STATES: MemoryState[] = ['on', 'stale', 'off'];

function generateComplianceMetrics(
  seed: number,
  roster: DemoUserPattern[]
): DemoComplianceMetric[] {
  faker.seed(seed + 2);
  const lastWeekIdx = WEEKS_GENERATED - 1;
  return roster.map(u => {
    const excused = u.weeklyCapacityMinutes === 0;
    const totalHours = Math.round((u.weekMinutes[lastWeekIdx] ?? 0) / 6) / 10;
    const neverLogs = !excused && totalHours === 0;
    return {
      userId: u.id,
      name: u.name,
      weeklyCapacityHours: u.weeklyCapacityMinutes / 60,
      totalHours,
      automationRate:
        excused || neverLogs ? 0 : Math.round(faker.number.float({ min: 0, max: 0.9 }) * 100) / 100,
      memoryState: excused || neverLogs ? 'off' : faker.helpers.arrayElement(MEMORY_STATES),
      lastLoggedAgo:
        excused || neverLogs
          ? null
          : faker.helpers.arrayElement([
              { hours: faker.number.int({ min: 0, max: 8 }) },
              { days: faker.number.int({ min: 1, max: 4 }) },
              { minutes: faker.number.int({ min: 5, max: 55 }) },
            ]),
      lastSeenAgo: excused || neverLogs ? null : { hours: faker.number.int({ min: 0, max: 72 }) },
    };
  });
}

function agoIso(offset: { hours?: number; days?: number; minutes?: number } | null): string | null {
  if (!offset) return null;
  return DateTime.now().minus(offset).toISO();
}

function generateActivities(
  seed: number,
  people: GeneratedPerson[],
  projects: DemoProject[]
): Activity[] {
  faker.seed(seed + 3);
  const now = DateTime.now();
  const activityKinds = [
    AnomalyActivityTypes.user_logged_odd_duration,
    RegularActivityTypes.daily_reminder,
    RegularActivityTypes.create_project,
    RegularActivityTypes.logged_first_time,
    AnomalyActivityTypes.user_logged_rare_project,
    AnomalyActivityTypes.user_logged_odd_time_from,
  ];
  // One activity per distinct archetype actually present in the roster
  // (first person encountered for each), so the feed narrates a real spread
  // of situations regardless of which archetypes the weighted draw happened
  // to produce — never just whoever landed in the first few roster slots.
  const seenArchetypes = new Set<string>();
  const representatives = people.filter(p => {
    if (seenArchetypes.has(p.archetypeKey)) return false;
    seenArchetypes.add(p.archetypeKey);
    return true;
  });
  return representatives.map((rep, i) => {
    const user = rep.pattern;
    const project = projects[i % projects.length];
    const kind = activityKinds[i % activityKinds.length];
    const daysAgo = faker.number.int({ min: 0, max: 3 });
    const date = now.minus({ days: daysAgo, hours: faker.number.int({ min: 0, max: 6 }) });
    return {
      id: 90001 + i,
      type: kind,
      detail: `Activity from ${user.name} on ${project.name}`,
      message: `${user.name} — ${project.name}`,
      notable: i % 2 === 0,
      isAnomaly:
        kind === AnomalyActivityTypes.user_logged_odd_duration ||
        kind === AnomalyActivityTypes.user_logged_rare_project ||
        kind === AnomalyActivityTypes.user_logged_odd_time_from,
      client: { id: project.clientId, name: project.clientName, color: '#888', isArchived: false },
      project: {
        id: project.id,
        name: project.name,
        color: project.color,
        isDeleted: false,
        client: { id: project.clientId, name: project.clientName, color: '#888' },
      },
      user: { id: user.id, name: user.name, avatar: makeInitialsAvatar(user.name) },
      url: '#',
      relativeTime: daysAgo === 0 ? 'Today' : `${daysAgo}d ago`,
      relativeDay: daysAgo === 0 ? 'Today' : date.toFormat('EEE, dd MMM'),
      date: date.toISO() ?? '',
    };
  });
}

interface DemoTeamDef {
  name: string;
  emoji: string;
}

const TEAM_DEFS: DemoTeamDef[] = [
  { name: 'Product', emoji: '🚀' },
  { name: 'Design', emoji: '🎨' },
  { name: 'Engineering', emoji: '🛠️' },
  { name: 'Marketing', emoji: '📣' },
  { name: 'Customer Success', emoji: '🤝' },
];

/**
 * Spreads the roster across TEAM_DEFS.length teams, roughly evenly, with
 * shuffled roster positions — not a fixed `i % teamCount` rotation, which
 * would put the same relative roster positions on the same team every time
 * (see assignArchetypes above for the same lesson applied to personas).
 */
function assignTeamIndices(size: number): number[] {
  const indices = Array.from({ length: size }, (_, i) => i % TEAM_DEFS.length);
  return faker.helpers.shuffle(indices);
}

/** All 5 demo teams with resolved members. One team per person; the first
 *  member assigned to a team (in roster order) is its lead. */
function generateTeams(seed: number, roster: DemoUserPattern[]): TeamWithUsers[] {
  faker.seed(seed + 4);
  const teamIndexByPersonIdx = assignTeamIndices(roster.length);

  return TEAM_DEFS.map((def, teamIdx) => {
    const memberIndices = teamIndexByPersonIdx
      .map((assignedTeamIdx, personIdx) => ({ assignedTeamIdx, personIdx }))
      .filter(({ assignedTeamIdx }) => assignedTeamIdx === teamIdx)
      .map(({ personIdx }) => personIdx);
    const [leadPersonIdx] = memberIndices;

    const teamUsers: TeamUser[] = memberIndices.map(personIdx => {
      const person = roster[personIdx];
      const avatar = makeInitialsAvatar(person.name);
      return personIdx === leadPersonIdx
        ? { id: person.id, name: person.name, avatar, isTeamLead: true as const }
        : { id: person.id, name: person.name, avatar, isTeamLead: false as const };
    });

    return {
      id: -(teamIdx + 1),
      name: def.name,
      emoji: makeEmojiImage(def.emoji),
      users: memberIndices.map(personIdx => ({
        user_id: roster[personIdx].id,
        lead: personIdx === leadPersonIdx,
      })),
      teamUsers,
    };
  });
}

interface DemoRosterBundle {
  roster: DemoUserPattern[];
  rosterById: Map<number, DemoUserPattern>;
  invited: DemoInvitedPerson[];
  invitedById: Map<number, DemoInvitedPerson>;
  projects: DemoProject[];
  complianceMetrics: DemoComplianceMetric[];
  activities: Activity[];
  teams: TeamWithUsers[];
}

const bundleCache = new Map<string, DemoRosterBundle>();

function getRosterBundle(seed: number, size: number): DemoRosterBundle {
  const cacheKey = `${seed}:${size}`;
  const cached = bundleCache.get(cacheKey);
  if (cached) return cached;

  const people = generateDemoPeople(seed, size);
  const roster = people.map(p => p.pattern);
  const projects = generateProjects(seed);
  const invited = generateInvitedPeople(seed, DEMO_INVITED_COUNT);
  const bundle: DemoRosterBundle = {
    roster,
    rosterById: new Map(roster.map(u => [u.id, u])),
    invited,
    invitedById: new Map(invited.map(p => [p.id, p])),
    projects,
    complianceMetrics: generateComplianceMetrics(seed, roster),
    activities: generateActivities(seed, people, projects),
    teams: generateTeams(seed, roster),
  };
  bundleCache.set(cacheKey, bundle);
  return bundle;
}

function currentBundle(): DemoRosterBundle {
  return getRosterBundle(DEMO_GENERATOR_SEED, DEMO_TEAM_SIZE);
}

// ─── Dashboard tab ────────────────────────────────────────────────────────────

export function buildPeopleDemoData(
  since: string,
  period: number
): {
  usersLoaded: ItemStatusWrapper<UserViewModel>[];
  graphData: HoursPerWeekData[];
  userCapacityMap: Record<number, number>;
  userCapacityChangedMap: Record<number, { hasChanged: boolean }>;
} {
  const { roster } = currentBundle();

  const weekRanges: Array<{ since: string; until: string }> = [];
  let weekStart = fromISO(since);
  for (let i = 0; i < period; i++) {
    const weekEnd = weekStart.plus({ days: 6 });
    weekRanges.push({ since: weekStart.toISODate(), until: weekEnd.toISODate() });
    weekStart = weekStart.plus({ days: 7 });
  }

  const usersLoaded: ItemStatusWrapper<UserViewModel>[] = roster.map(u => {
    const byWeek: WeekSummaryWithRange[] = weekRanges.map((wr, i) => {
      const minutes = u.weekMinutes[i] ?? 0;
      const billable = Math.round(minutes * 0.8);
      const nonBillable = minutes - billable;
      return {
        week: `${wr.since}-${wr.until}`,
        from: wr.since,
        to: wr.until,
        capacity: makeDuration(u.weeklyCapacityMinutes),
        duration: makeDuration(minutes),
        billable_duration: makeDuration(billable),
        non_billable_duration: makeDuration(nonBillable),
        total_duration: makeDuration(minutes),
        planned_duration: ZERO,
        tasked_duration: ZERO,
      };
    });

    const totalMinutes = weekRanges.reduce((sum, _, i) => sum + (u.weekMinutes[i] ?? 0), 0);
    const totalBillable = Math.round(totalMinutes * 0.8);
    const totalNonBillable = totalMinutes - totalBillable;

    const summary: UserSummaryView = {
      updated_at: new Date().toISOString(),
      totals: {
        updated_at: new Date().toISOString(),
        weekly_capacity: makeDuration(u.weeklyCapacityMinutes),
        duration: makeDuration(totalMinutes),
        billable_duration: makeDuration(totalBillable),
        non_billable_duration: makeDuration(totalNonBillable),
        total_duration: makeDuration(totalMinutes),
        planned_duration: ZERO,
        tasked_duration: ZERO,
      },
      byWeek,
    };

    return {
      status: READY,
      item: {
        id: u.id,
        name: u.name,
        url: `/users/${u.id}`,
        avatar: makeInitialsAvatar(u.name),
        summary,
        active: true,
        deleted: false,
        user_level: u.userLevel,
        weekly_capacity: u.weeklyCapacityMinutes / 60,
      },
    };
  });

  const graphData: HoursPerWeekData[] = weekRanges.map((wr, i) => {
    const totalCapacity = roster.reduce((sum, u) => sum + u.weeklyCapacityMinutes, 0);
    const totalMinutes = roster.reduce((sum, u) => sum + (u.weekMinutes[i] ?? 0), 0);
    const billable = Math.round(totalMinutes * 0.8);
    const nonBillable = totalMinutes - billable;

    const overtimeStack = Math.max(totalMinutes - totalCapacity, 0);
    let billableStack = billable;
    let nonBillableStack = nonBillable;
    if (overtimeStack > 0) {
      nonBillableStack = Math.max(nonBillableStack - overtimeStack, 0);
      if (nonBillableStack + billableStack > totalCapacity) {
        billableStack = totalCapacity - nonBillableStack;
      }
    }
    const loggedStack = billableStack + nonBillableStack;
    const remainingCapacityStack = Math.max(totalCapacity - totalMinutes, 0);

    return {
      weekStart: wr.since,
      weekEnd: wr.until,
      billable,
      nonBillable,
      planned: 0,
      tasked: 0,
      total: totalMinutes,
      capacity: totalCapacity,
      loggedStack,
      remainingCapacityStack,
      overtimeStack,
      overschedulingStack: 0,
      nonBillableStack,
      billableStack,
      plannedStack: 0,
      totalStack: remainingCapacityStack + overtimeStack + loggedStack,
    };
  });

  const userCapacityMap: Record<number, number> = {};
  const userCapacityChangedMap: Record<number, { hasChanged: boolean }> = {};
  for (const u of roster) {
    userCapacityMap[u.id] = (u.weeklyCapacityMinutes / 60) * period;
    userCapacityChangedMap[u.id] = { hasChanged: false };
  }

  return { usersLoaded, graphData, userCapacityMap, userCapacityChangedMap };
}

// ─── All Users tab ────────────────────────────────────────────────────────────

export const DEMO_PEOPLE_COUNT = DEMO_TEAM_SIZE;
export const DEMO_INVITED_PEOPLE_COUNT = DEMO_INVITED_COUNT;

const LIST_WEEK_IDX = WEEKS_GENERATED - 1;

function demoListWeekHours(u: DemoUserPattern): {
  loggedHours: number;
  billableHours: number;
  capacityHours: number;
} {
  const loggedHours = Math.round(((u.weekMinutes[LIST_WEEK_IDX] ?? 0) / 60) * 100) / 100;
  const billableHours = Math.round(loggedHours * 0.8 * 100) / 100;
  const capacityHours = u.weeklyCapacityMinutes / 60;
  return { loggedHours, billableHours, capacityHours };
}

function demoControlDataForUser(u: DemoUserPattern, dayDates: ReadonlyArray<string>): ControlData {
  const dailyCapacityHours = Math.round((u.weeklyCapacityMinutes / 60 / 5) * 100) / 100;
  const control: ControlData = {};
  for (const date of dayDates) {
    const isoWeekday = DateTime.fromISO(date).weekday;
    const hours = u.dailyHoursMonToSun[isoWeekday - 1] ?? 0;
    if (hours <= 0) continue;
    const billable = Math.round(hours * 0.8 * 100) / 100;
    control[date] = {
      duration: hours,
      total_duration: hours,
      billable_duration: billable,
      non_billable_duration: Math.round((hours - billable) * 100) / 100,
      capacity: dailyCapacityHours,
    };
  }
  return control;
}

export function buildListDemoUsers(dayDates: ReadonlyArray<string> = []): ListUser[] {
  const { roster } = currentBundle();
  return roster.map(u => {
    const { loggedHours, billableHours, capacityHours } = demoListWeekHours(u);

    const summary: DurationSummary & { control: ControlData } = {
      duration: loggedHours,
      billable_duration: billableHours,
      non_billable_duration: Math.round((loggedHours - billableHours) * 100) / 100,
      total_duration: loggedHours,
      capacity: capacityHours,
      total_capacity: capacityHours,
      planned_duration: 0,
      tasked_duration: 0,
      control: demoControlDataForUser(u, dayDates),
    };

    return {
      id: u.id,
      name: u.name,
      email: `${u.name.toLowerCase().replace(/\s+/g, '.')}@demo.example`,
      active: true,
      avatar: makeInitialsAvatar(u.name),
      updated_at: Date.now(),
      external_id: '',
      default_hour_rate: 0,
      internal_hour_rate: 0,
      day_view_onboarded: true,
      memory_onboarded: true,
      created_at: Date.now() - 86400000 * 30,
      last_received_memories_date: null,
      role_id: 1,
      admin: u.userLevel === 'admin',
      hide_hourly_rate: false,
      hide_internal_hourly_rate: false,
      sign_in_count: 10,
      time_zone: 'UTC',
      owner: false,
      time_format: UserTimeFormat.H24,
      date_format: 'dmy' as const,
      properties: {},
      deleted: false,
      weekly_capacity: u.weeklyCapacityMinutes / 60,
      work_days: 'MON,TUE,WED,THU,FRI',
      memory_retention_days: null,
      isLoaded: true,
      status: READY,
      url: `/users/${u.id}`,
      summary,
    };
  });
}

/**
 * Rows for the Invited tab — pending invites who've never logged in, so
 * every hours/capacity field reads zero and `active` is false. `UserRow`
 * renders these as an "Invite pending" label instead of the hours grid, and
 * offers "Re-invite user" from the row's "..." menu, matching a real pending
 * invite.
 */
export function buildInvitedListDemoUsers(): ListUser[] {
  const { invited } = currentBundle();
  const zeroSummary: DurationSummary & { control: ControlData } = {
    duration: 0,
    billable_duration: 0,
    non_billable_duration: 0,
    total_duration: 0,
    capacity: 0,
    total_capacity: 0,
    planned_duration: 0,
    tasked_duration: 0,
    control: {},
  };

  return invited.map(p => ({
    id: p.id,
    name: p.name,
    email: `${p.name.toLowerCase().replace(/\s+/g, '.')}@demo.example`,
    active: false,
    avatar: makeInitialsAvatar(p.name),
    updated_at: Date.now(),
    external_id: '',
    default_hour_rate: 0,
    internal_hour_rate: 0,
    day_view_onboarded: false,
    memory_onboarded: false,
    created_at: Date.now() - 86400000 * p.invitedDaysAgo,
    last_received_memories_date: null,
    role_id: 1,
    admin: false,
    hide_hourly_rate: false,
    hide_internal_hourly_rate: false,
    sign_in_count: 0,
    time_zone: 'UTC',
    owner: false,
    time_format: UserTimeFormat.H24,
    date_format: 'dmy' as const,
    properties: {},
    deleted: false,
    weekly_capacity: 0,
    work_days: 'MON,TUE,WED,THU,FRI',
    memory_retention_days: null,
    isLoaded: true,
    status: READY,
    url: `/users/${p.id}`,
    summary: zeroSummary,
  }));
}

export function buildListDemoTotals(): { totals: DurationSummary; totalCapacity: number } {
  const { roster } = currentBundle();
  let duration = 0;
  let billable = 0;
  let capacity = 0;
  for (const u of roster) {
    const week = demoListWeekHours(u);
    duration += week.loggedHours;
    billable += week.billableHours;
    capacity += week.capacityHours;
  }
  duration = Math.round(duration * 100) / 100;
  billable = Math.round(billable * 100) / 100;
  capacity = Math.round(capacity * 100) / 100;

  const totals: DurationSummary = {
    duration,
    billable_duration: billable,
    non_billable_duration: Math.round((duration - billable) * 100) / 100,
    total_duration: duration,
    capacity,
    total_capacity: capacity,
    planned_duration: 0,
    tasked_duration: 0,
  };
  return { totals, totalCapacity: capacity };
}

// ─── Activities tab ───────────────────────────────────────────────────────────

export function buildActivitiesDemoData(): Activity[] {
  return currentBundle().activities;
}

// ─── Compliance tab ───────────────────────────────────────────────────────────

export function buildComplianceDemoUsers(): ComplianceUserMetric[] {
  return currentBundle().complianceMetrics.map(m => ({
    user_id: m.userId,
    name: m.name,
    email: `${m.name.toLowerCase().replace(/\s+/g, '.')}@demo.example`,
    weekly_capacity_hours: m.weeklyCapacityHours,
    total_hours: m.totalHours,
    ai_hours: Math.round(m.totalHours * m.automationRate * 10) / 10,
    automation_rate: m.automationRate,
    last_seen_at: agoIso(m.lastSeenAgo),
    memory_state: m.memoryState,
    last_hours_logged_at: agoIso(m.lastLoggedAgo),
  }));
}

// ─── Single-user detail page ─────────────────────────────────────────────────

export const DEMO_DETAIL_USER_IDS: ReadonlySet<number> = demoIdRange(DEMO_TEAM_SIZE);

/**
 * Pending invites also have a `url` in the Invited tab's rows (`UserRow`
 * links every row, active or not), so their detail page must resolve as a
 * demo id too — otherwise navigating to one would fall through to the real
 * (nonexistent) user lookup and re-open the "leaves the browser" problem
 * this whole preset exists to avoid. They carry no hours/team data, so the
 * builders below return an empty/pending view instead of throwing.
 */
export const DEMO_INVITED_USER_IDS: ReadonlySet<number> = demoIdRange(
  DEMO_INVITED_COUNT,
  DEMO_TEAM_SIZE
);

function requireRosterPerson(userId: number): DemoUserPattern {
  const person = currentBundle().rosterById.get(userId);
  if (!person) {
    throw new Error(
      `usersPage.demo.generator: ${userId} is not a demo id — deep data must only be requested for ids in DEMO_DETAIL_USER_IDS or DEMO_INVITED_USER_IDS`
    );
  }
  return person;
}

const ROLE_NAME_BY_LEVEL: Record<string, string> = {
  admin: 'Admin',
  manager: 'Manager',
  member: 'Employee',
};

function buildInvitedEnrichedUser(p: DemoInvitedPerson): EnrichedUser {
  const avatarUri = makeInitialsAvatar(p.name);
  return {
    id: p.id,
    email: `${p.name.toLowerCase().replace(/\s+/g, '.')}@demo.example`,
    name: p.name,
    active: false,
    avatar: { large_retina: avatarUri, medium_retina: avatarUri },
    updated_at: Date.now(),
    external_id: '',
    default_hour_rate: 0,
    internal_hour_rate: 0,
    day_view_onboarded: false,
    memory_onboarded: false,
    created_at: Date.now() - 86400000 * p.invitedDaysAgo,
    last_received_memories_date: null,
    role_id: 1,
    admin: false,
    hide_hourly_rate: false,
    hide_internal_hourly_rate: false,
    sign_in_count: 0,
    time_zone: 'UTC',
    owner: false,
    time_format: UserTimeFormat.H24,
    date_format: 'dmy' as const,
    properties: {},
    deleted: false,
    weekly_capacity: 0,
    work_days: 'MON,TUE,WED,THU,FRI',
    memory_retention_days: null,
    role_name: 'Employee',
  };
}

export function buildDemoEnrichedUser(userId: number): EnrichedUser {
  const invited = currentBundle().invitedById.get(userId);
  if (invited) {
    return buildInvitedEnrichedUser(invited);
  }
  const u = requireRosterPerson(userId);
  const avatarUri = makeInitialsAvatar(u.name);
  return {
    id: u.id,
    email: `${u.name.toLowerCase().replace(/\s+/g, '.')}@demo.example`,
    name: u.name,
    active: true,
    avatar: { large_retina: avatarUri, medium_retina: avatarUri },
    updated_at: Date.now(),
    external_id: '',
    default_hour_rate: 0,
    internal_hour_rate: 0,
    day_view_onboarded: true,
    memory_onboarded: true,
    created_at: Date.now() - 86400000 * 90,
    last_received_memories_date: null,
    role_id: 1,
    admin: u.userLevel === 'admin',
    hide_hourly_rate: false,
    hide_internal_hourly_rate: false,
    sign_in_count: 42,
    time_zone: 'UTC',
    owner: false,
    time_format: UserTimeFormat.H24,
    date_format: 'dmy' as const,
    properties: {},
    deleted: false,
    weekly_capacity: u.weeklyCapacityMinutes / 60,
    work_days: 'MON,TUE,WED,THU,FRI',
    memory_retention_days: null,
    role_name: ROLE_NAME_BY_LEVEL[u.userLevel] ?? 'Employee',
  };
}

export function buildDemoTeamsForUser(userId: number): Team[] {
  // A pending invite has no team yet.
  if (currentBundle().invitedById.has(userId)) {
    return [];
  }
  requireRosterPerson(userId);
  return currentBundle().teams.filter(team => team.users.some(u => u.user_id === userId));
}

/**
 * All 5 demo teams with resolved members, used to power the People page's
 * "Manage teams" modal under the preset instead of the account's real
 * teams, which have nothing to do with the fictional roster.
 */
export function buildTeamsWithUsers(): TeamWithUsers[] {
  return currentBundle().teams;
}

// ─── Per-person deep data (lazy, memoized by demo id) ────────────────────────

const DEMO_HOURLY_RATE = 95;

export interface DemoReportChartDay {
  day: number;
  hours: number;
}

export interface DemoTimesheetRow {
  date: string;
  projectName: string;
  projectColor: string;
  clientName: string;
  hours: number;
  tag?: string;
}

export interface DemoReportProjectRow {
  clientName: string;
  projectName: string;
  color: string;
  hours: number;
  money: number;
}

export interface DemoReportTagRow {
  tagName: string;
  hours: number;
  money: number;
}

interface DeepPersonView {
  dashboardTotals: { summary: CostAndDurationSummary; capacityHours: number };
  reportChart: DemoReportChartDay[];
  reportTotals: { totalHours: number; totalMoney: number };
  timesheetRows: DemoTimesheetRow[];
  reportProjects: DemoReportProjectRow[];
  reportTags: DemoReportTagRow[];
}

const deepDataCache = new Map<number, DeepPersonView>();

/** Test-only seam: lets tests confirm deep data hasn't been (re)generated
 *  without reaching into the module-private cache. */
export function hasCachedDeepDataForTests(userId: number): boolean {
  return deepDataCache.has(userId);
}

/** Test-only seam: clears the per-person memoization cache so a test can
 *  prove regeneration is deterministic, independent of memoization. */
export function resetDeepDataCacheForTests(): void {
  deepDataCache.clear();
}

/** A pending invite has never logged anything — every Timesheets/Report field
 *  reads empty/zero rather than going through the roster-only builder below. */
const EMPTY_DEEP_PERSON_VIEW: DeepPersonView = {
  dashboardTotals: {
    summary: {
      minutesLogged: 0,
      minutesBillable: 0,
      minutesNonBillable: 0,
      minutesPlanned: 0,
      minutesBilled: 0,
      minutesUnbilled: 0,
      costPlanned: 0,
      costLogged: 0,
      costBilled: 0,
      costUnbilled: 0,
    },
    capacityHours: 0,
  },
  reportChart: [],
  reportTotals: { totalHours: 0, totalMoney: 0 },
  timesheetRows: [],
  reportProjects: [],
  reportTags: [],
};

/**
 * Builds (and caches) one demo person's full Timesheets/Report view. Called
 * only from the buildDetail* functions below, i.e. only once that person's
 * detail page is actually opened — never during roster build. Reseeds
 * independently per person (seed + userId) so a given id's deep data never
 * depends on when, or in what order, other people were opened. Cached as a
 * whole so every buildDetail* call for an already-opened person returns the
 * exact same array/object references, not just deeply-equal copies.
 */
function getOrBuildDeepPersonView(userId: number): DeepPersonView {
  const cached = deepDataCache.get(userId);
  if (cached) return cached;

  if (currentBundle().invitedById.has(userId)) {
    deepDataCache.set(userId, EMPTY_DEEP_PERSON_VIEW);
    return EMPTY_DEEP_PERSON_VIEW;
  }

  const u = requireRosterPerson(userId);
  const { projects } = currentBundle();

  // Only days that have actually happened — the whole calendar month would
  // put logged hours on days still in the future relative to today.
  const today = DateTime.now();
  const start = today.startOf('month');
  const daysElapsed = today.day;
  const days: Array<{ date: DateTime; hours: number }> = [];
  for (let i = 0; i < daysElapsed; i++) {
    const date = start.plus({ days: i });
    days.push({ date, hours: u.dailyHoursMonToSun[date.weekday - 1] ?? 0 });
  }

  faker.seed(DEMO_GENERATOR_SEED + userId);
  const projectByDayIndex = days.map(() => faker.number.int({ min: 0, max: projects.length - 1 }));

  const workDayIndices = days.map((d, i) => ({ d, i })).filter(({ d }) => d.hours > 0);

  const workDaysInMonth = days.filter(d => d.date.weekday <= 5).length;
  const dailyCapacityHours = u.weeklyCapacityMinutes / 60 / 5;
  const capacityHours = Math.round(dailyCapacityHours * workDaysInMonth * 10) / 10;
  const totalLoggedHours = Math.round(days.reduce((sum, d) => sum + d.hours, 0) * 10) / 10;
  const billableHours = Math.round(totalLoggedHours * 0.8 * 10) / 10;
  const minutesLogged = Math.round(totalLoggedHours * 60);
  const costLogged = Math.round(totalLoggedHours * DEMO_HOURLY_RATE * 100) / 100;

  const reportTotalHours = Math.round(days.reduce((sum, d) => sum + d.hours, 0) * 100) / 100;

  const timesheetRows: DemoTimesheetRow[] = workDayIndices
    .map(({ d, i }) => {
      const project = projects[projectByDayIndex[i] % projects.length];
      return {
        date: d.date.toISODate() ?? '',
        projectName: project.name,
        projectColor: project.color,
        clientName: project.clientName,
        hours: d.hours,
        tag: i % 3 === 0 ? 'Design' : undefined,
      };
    })
    .sort((a, b) => (a.date < b.date ? 1 : -1));

  const hoursByProject = new Map<string, number>();
  workDayIndices.forEach(({ d, i }) => {
    const project = projects[projectByDayIndex[i] % projects.length];
    hoursByProject.set(project.name, (hoursByProject.get(project.name) ?? 0) + d.hours);
  });
  const reportProjects: DemoReportProjectRow[] = projects
    .map(p => {
      const hours = Math.round((hoursByProject.get(p.name) ?? 0) * 100) / 100;
      return {
        clientName: p.clientName,
        projectName: p.name,
        color: p.color,
        hours,
        money: Math.round(hours * DEMO_HOURLY_RATE * 100) / 100,
      };
    })
    .filter(p => p.hours > 0);

  let designHours = 0;
  let untaggedHours = 0;
  workDayIndices.forEach(({ d }, i) => {
    if (i % 3 === 0) designHours += d.hours;
    else untaggedHours += d.hours;
  });
  designHours = Math.round(designHours * 100) / 100;
  untaggedHours = Math.round(untaggedHours * 100) / 100;
  const reportTags: DemoReportTagRow[] = [
    {
      tagName: 'Design',
      hours: designHours,
      money: Math.round(designHours * DEMO_HOURLY_RATE * 100) / 100,
    },
    {
      tagName: 'Untagged',
      hours: untaggedHours,
      money: Math.round(untaggedHours * DEMO_HOURLY_RATE * 100) / 100,
    },
  ].filter(t => t.hours > 0);

  const view: DeepPersonView = {
    dashboardTotals: {
      summary: {
        minutesLogged,
        minutesBillable: Math.round(billableHours * 60),
        minutesNonBillable: Math.round((totalLoggedHours - billableHours) * 60),
        minutesPlanned: 0,
        minutesBilled: 0,
        minutesUnbilled: minutesLogged,
        costPlanned: 0,
        costLogged,
        costBilled: 0,
        costUnbilled: costLogged,
      },
      capacityHours,
    },
    reportChart: days.map(d => ({ day: d.date.day, hours: d.hours })),
    reportTotals: {
      totalHours: reportTotalHours,
      totalMoney: Math.round(reportTotalHours * DEMO_HOURLY_RATE * 100) / 100,
    },
    timesheetRows,
    reportProjects,
    reportTags,
  };
  deepDataCache.set(userId, view);
  return view;
}

export function buildDetailDashboardTotals(userId: number): {
  summary: CostAndDurationSummary;
  capacityHours: number;
} {
  return getOrBuildDeepPersonView(userId).dashboardTotals;
}

export function buildDetailReportChart(userId: number): DemoReportChartDay[] {
  return getOrBuildDeepPersonView(userId).reportChart;
}

export function buildDetailReportTotals(userId: number): {
  totalHours: number;
  totalMoney: number;
} {
  return getOrBuildDeepPersonView(userId).reportTotals;
}

export function buildDetailTimesheetRows(userId: number): DemoTimesheetRow[] {
  return getOrBuildDeepPersonView(userId).timesheetRows;
}

export function buildDetailReportProjects(userId: number): DemoReportProjectRow[] {
  return getOrBuildDeepPersonView(userId).reportProjects;
}

export function buildDetailReportTags(userId: number): DemoReportTagRow[] {
  return getOrBuildDeepPersonView(userId).reportTags;
}
